<template>
  <div class="login">

    <div class="introduction">

      <div class="overtitle">Committed to Software Quality in Research</div>
      <div class="title">Streamline the Adoption of Novel Software Engineering Practices in Research and Receive Credit for It</div>
      <div>The adoption of quality-based practices is one common struggle when it comes to developing software in research environments. The SQAaaS platform <b>provides researchers with ready-to-use <em>CI/CD pipelines</em></b> that cross-check the relevant quality criteria any software project ought to fulfill in order to meet the Open Science values, and thus be sustainable, reliable, usable, reusable and discoverable in the long term. Additionally, the SQAaaS features an assessment tool that acknowledges the quality attributes of the software, <b>enabling proper recognition through shareable and verifiable <em>digital badges</em></b>.</div>
      <div>The SQAaaS platform is developed by CSIC, LIP and UPV.</div>
      <div class="developers">
        <a href="https://www.csic.es/en" target="_blank">      <img src="../../static/logo-csic.png" alt="CSIC" height="60"> </a>
        <a href="https://www.lip.pt/?lang=en" target="_blank"> <img src="../../static/logo-lip.png" alt="LIP" height="60">   </a>
        <a href="https://www.upv.es/en" target="_blank">       <img src="../../static/logo-upv.png" alt="UPV" height="60">   </a>
      </div>
      <a type="button" class="btn-outline btn youtube-button" href="https://www.youtube.com/watch?v=krHm0eP9yiI" target="_blank"> <i class="fab fa-youtube"></i>See SQAaaS in Action </a>

    </div>

    <div class="section intro">
      <h2>What are CI/CD pipelines?</h2>
      <p>CI/CD stands for Continuous Integration and Continuous Delivery, respectively. They are common practices in software engineering that aim at improving the overall quality of the software during the development life cycle. CI/CD encompass both the verification and validation (V&V) processes, and thus they not only act at the code level, but also assess the operational part of the software. Consequently, CI/CD helps developers to meet both the functional (behavior-driven) and non-functional (usability-oriented) requirements that the software is intended to provide to their users.</p>
      <h4>What about the Pipelines?</h4>
      <p>The CI/CD work is commonly arranged into pipelines, which define the sequential steps that the software V&V needs to go through, commonly as often as new source code changes are added to the codebase. As a result, automation is key to enable the CI/CD scenarios and a key feature of the pipelines.</p>
    </div>

    <div class="section intro">
      <h2>What are SQAaaS digital badges all about?</h2>
      <p>Badges are commonly used by earners for proving the attainment of certain achievements. Digital badges complement traditional badging by providing virtual certification with built-in features for online sharing, verification, portability and trustworthiness. The SQAaaS platform harnesses the potential of the Open Badges specification to issue digital badges for particular versions of the software. Each badge issued by SQAaaS has associated metadata that includes the software quality criteria being fulfilled and the applicable evidence in the form of build logs, reports and/or binary artefacts.</p>
      <h4>How can software in research benefit from badges?</h4>
      <p>The resultant badge can then be shared and verified by external bodies, thus significantly impacting the visibility and adoption of the software. In research ecosystems, such a quality badge definitely contributes to usability, reputation-building and crediting, as well as improving the reusability and reproducibility of the awarded software version.</p>
    </div>

    <div class="section blue">
      <h2>Benefit from the good practices when developing software and services</h2>
      <h4>Meet the quality criteria we use to foster the implementation of reproducible and sustainable research software.</h4>
      <p>The workflow of the CI/CD pipelines created by the SQAaaS platform is criteria-driven, which means that each stage executed by the pipeline fulfills a criterion from the two sets of Software and Service Quality Assurance documents. The criteria compiled are the result of an open and continuous collaborative effort and can be reached at:</p>
      <p>• A set of Common Software Quality Assurance Baseline Criteria for <a href="https://github.com/indigo-dc/sqa-baseline" target="_blank">Research Projects</a></p>
      <p>• A set of Common Service Quality Assurance Baseline Criteria for <a href="https://github.com/EOSC-synergy/service-qa-baseline" target="_blank">Research Projects</a></p>
    </div>

    <div class="section white">
      <h2>Tailor quality checks to your software needs</h2>
      <h4>No expertise required on CI/CD, just graphically compose the pipeline, add it to your code repository, and start validating your software for every change</h4>
      <p>Researchers and computational scientists use SQAaaS to harness the benefits of applying good software engineering practices to their own software projects. The <strong> <i> Pipeline as a Service </i></strong> building block allows you to compose and test customized CI/CD pipelines in accordance with reference criteria used throughout the software development life cycle of research EU projects. And all of this without the burden of being at the forefront of the latest trendy CI languages or configuration formats.</p>
      <div> <button type="button" class="btn btn-outline" @click="loginEGI()">Try it out!</button> </div>
    </div>

    <div class="section blue">
      <h2>Obtain the quality achievements of your software</h2>
      <h4>Estimate how well your software is doing in terms of QA and get recognition for it!</h4>
      <p>The <strong><i> Quality Assessment & Awarding</i></strong> building block analyses, building upon your input, the level of completeness with the [reference criteria](link) for any given release of your software. Once the assessment is completed, you will get a digital badge that acknowledges the criteria fulfilled and the associated assets such as the code versioning and building related data.</p>
      <div> <button type="button" class="btn btn-outline" @click="loginEGI()">Try it out!</button> </div>
    </div>

    <div class="section white">
      <h2>Deploy your own SQAaaS platform</h2>
      <h4>Get the SQAaaS on your premises at the drop of a hat!</h4>
      <p>The SQAaaS platform relies on a set of services to provide its functionality. We deliver an Infrastructure as Code solution that makes the on-premise deployment straightforward for any individual researcher or scientific community. This approach is useful for use cases with privative source code where unrestricted access to the repositories is not granted.</p>
      <div>
        <a href="https://github.com/eosc-synergy/SQAaaS" target="_blank">
          <button type="button" class="btn btn-outline">Try it out!</button>
        </a>
      </div>
    </div>

    <div class="section blue">
      <h2>Use code rather than a graphical interface</h2>
      <h4>Harness the full potential of the underlying CI/CD technology by means of the JePL library</h4>
      <div>Tech-savvy users tend to favor code over a graphical interface for the task of managing their CI/CD pipelines. Especially suitable for complex setups, you can use the same library the SQAaaS platform relies on, the <strong><i>JePL library</i></strong>, and benefit from the full set of features it provides.</div>
      <div>
        <a href="https://github.com/indigo-dc/jenkins-pipeline-library" target="_blank">
          <button type="button" class="btn btn-outline">Try it out!</button>
        </a>
      </div>
    </div>

    <div class="section">
      <h2 >Learn more about SQAaaS</h2>

      <div class="learn-more">

        <div class="documentation">
          <a href="https://docs.sqaaas.eosc-synergy.eu/" target="_blank">
            <span class="fa-stack fa-3x">
              <i class="fas fa-circle fa-stack-2x" />
              <i class="fas fa-book fa-stack-1x" />
            </span>
          </a>
          <h4>SQAaaS Documentation</h4>
          <div>Checks the docs to understand all the capabilities of SQAaaS</div>
      </div>

        <div class="github">
          <a href="https://github.com/eosc-synergy/SQAaaS" target="_blank">
            <span class="fa-stack fa-3x">
              <i class="fas fa-circle fa-stack-2x" />
              <i class="fab fa-github fa-stack-1x" />
            </span>
          </a>
          <h4>GitHub</h4>
          <p>Open Source (GPLv3 License)</p>
        </div>

        <div class="youtube">
          <a href="https://www.youtube.com/watch?v=krHm0eP9yiI" target="_blank">
            <span class="fa-stack fa-3x">
              <i class="fas fa-circle fa-stack-2x" />
              <i class="fab fa-youtube fa-stack-1x" />
            </span>
          </a>
          <h4>YouTube</h4>
          <p>Find tutorials about SQAaaS</p>
        </div>

      </div>

    </div>

  </div>
</template>

<script>

  import ContentFooter from '../layout/ContentFooter'
  import TopNavbar from '../layout/TopNavbar.vue'
  import Services from '../services/services';
  import store from "@/store";

  export default {

    components: {
      TopNavbar,
      ContentFooter,
    },

    mixins: [ Services ],

    data() {
      return {}
    },

    methods: {

      showLoginRequiredAlert(){
        this.$swal.fire({
          title: 'Sign In required',
          html: '<div>You need to sign in first to access the requested area.</div><div>You will be redirected afterwards.</div>',
          icon: 'warning',
          confirmButtonText: 'Sign in',
          confirmButtonColor: '#3085d6',
          showCancelButton: true,
        }).then((result) => {
               if(result.isDismissed) this.$router.replace({'query': null});
          else if(result.isConfirmed) this.loginEGI()
        })
      }
    },
    created(){
      sessionStorage.removeItem("redirect")
      localStorage.removeItem('session')
      store.state.isLogged = false
    },
    mounted(){
      if (this.$route.query.redirect) this.showLoginRequiredAlert()
    },
    beforeRouteUpdate(to, from, next){
      if (to.query.redirect) this.showLoginRequiredAlert()
      next()
    }

  }
</script>

<style scoped>

  .login {
    margin-top: 4em;

    .introduction {
      padding: 0 40% 1em 15%;
      background-image: url('../../static/opacity.png');
      background-origin: padding-box;
      background-repeat: no-repeat;
      background-position: bottom left 75%;
      background-size: min(45em, 50%);
      div { margin-bottom: 2rem; }

      .overtitle {
        text-transform: uppercase;
        font-weight: bold;
        font-size:16px;
      }
      .title {
        text-transform: uppercase;
        font-weight: bold;
        font-size: 44px;
        line-height: 1.25;
      }
      .developers {
        display: flex;
        gap: 2.5em;
      }
      .youtube-button { margin-top: 1em; }
      .youtube-button i { margin-right: 0.7em; }
      .youtube-button:hover i { color: #c4302b; }
    }

    .section {
      padding: 3em 15% 3em 15%;
      h2 {
        font-weight: bold;
        margin-top: 0;
        padding-bottom: 0.5em;
        text-align: center;
      }
      h4 {
        font-weight: bold;
        padding-bottom: 0.2em;
      }
      div:has(.btn){
        text-align: center;
        padding-top: 1em;
      }
      .btn {
        font-weight: bold;
        padding: 0.3em 1.5em;
        font-size: 1.3em;
        line-height: 1.6;
        border-radius: 5px;
      }
      .btn:hover {
        color: #AB0720 !important;
        border-color: #AB0720 !important;
      }
    }

    .section.intro{
      h2, h4 {
        text-align: start;
        color:#73bbdc;
      }
    }

    .section.blue {
      background-color: #73bbdc;
      h2 { color: white; }
      a { color: white; }
      .btn {
        color: #fff;
        border: 1px solid #fff;
      }
    }

    .section.white {
      h2 { color: #73bbdc; }
      .btn {
        color: #73bbdc;
        border: 1px solid #73bbdc;
      }
    }

    .learn-more{
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      column-gap: 2em;

      div { text-align: center; }
      .fa-stack i:first-child { color: #fed136; }
      .fa-stack i:nth-child(2) {
        color: white;
        transition: color 0.3s;
      }
      .documentation .fa-stack i:nth-child(2):hover { color: #64b8f4; }
      .github .fa-stack i:nth-child(2):hover { color: #303030; }
      .youtube .fa-stack i:nth-child(2):hover { color: #c4302b; }

      h4 { font-weight: bold; }
    }
  }


  @media (max-width: 1300px) {
    .login {
      .introduction {
        padding-right: 15%;
        background-position: bottom 10% right 10%;
      }
    }
  }

  @media (max-width: 950px) {
    .login {
      .introduction {
        background-image: none;
      }
    }
  }

  @media (max-width: 720px) {
    .login {
      .introduction {
        padding: 0 7% 1em 7%;
        .title {
          font-size: 36px;
          text-align: center;
        }
        .developers{
          flex-direction: column;
          align-items: center;
          gap: 1.5em;
        }
        .youtube-button{
          display: block;
          margin: 0 10%;
        }
      }
      .section {
        padding: 3em 7% 3em 7%;
        h2 { font-size: 32px;}
      }
    }

  }

</style>
